import { Spacing } from '@persona-id/design-tokens';
import { difference, intersection } from 'lodash';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ReactSelect from 'dashboard/components/ReactSelect';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { VerificationCheckConfiguration } from 'dashboard/forms/FormVerificationTemplateChecks/FormVerificationTemplateChecks';
import {
  DOCUMENT_TYPE_TO_DISPLAY_NAME,
  DocumentType,
  DocumentTypeCheckConfig,
} from 'dashboard/models/verification-check-config/document-type-detection';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import useToggle from 'lib/hooks/useToggle';

const StyledReactSelect = styled(ReactSelect<DocumentType[]>).attrs(() => ({ size: 'sm' }))`
  margin-top: ${Spacing[3]};
  margin-bottom: ${Spacing[3]};
`;

interface DocumentTypeConfigurationElementProps {
  checkConfig: DocumentTypeCheckConfig;
  editable: boolean;
  handleUpdateCheck: (check: VerificationCheckConfig) => void;
  overrideVerificationChecksAndExtractionsByDocumentType: (documentType: DocumentType) => void;
}

const DocumentTypeConfigurationElement = ({
  checkConfig,
  editable,
  handleUpdateCheck,
  overrideVerificationChecksAndExtractionsByDocumentType,
}: DocumentTypeConfigurationElementProps) => {
  const acceptedDocumentTypes = checkConfig['accepted-document-types'] || [];
  const [
    overridePromptModal,
    { enable: showOverridePromptModal, disable: hideOverridePromptModal },
  ] = useToggle(false);

  const updateAcceptedDocumentTypes = (updatedAcceptedDocumentTypes: DocumentType[]) => {
    const originalAcceptedDocumentTypes = checkConfig['accepted-document-types'];
    handleUpdateCheck({
      ...checkConfig,
      'accepted-document-types': updatedAcceptedDocumentTypes,
    } as DocumentTypeCheckConfig);

    const newDocumentTypes = difference(
      updatedAcceptedDocumentTypes,
      originalAcceptedDocumentTypes,
    );

    // Show override prompt modal if a social security card document type is newly included in the config
    if (
      intersection(newDocumentTypes, [
        DocumentType.SocialSecurityCardRestricted,
        DocumentType.SocialSecurityCardUnrestricted,
      ]).length > 0
    ) {
      showOverridePromptModal();
    }
  };
  return (
    <>
      <VerificationCheckConfiguration title='Accepted document types' disabled={!editable}>
        <StyledReactSelect
          options={Object.values(DocumentType).map((type) => {
            return {
              label: DOCUMENT_TYPE_TO_DISPLAY_NAME[type],
              value: type,
            };
          })}
          disabled={!editable}
          value={acceptedDocumentTypes}
          onChange={updateAcceptedDocumentTypes}
          isMulti
          portal
        />
      </VerificationCheckConfiguration>
      <ModalWrapper isOpen={overridePromptModal} handleCloseModal={hideOverridePromptModal}>
        <ConfirmationModal
          header="Would you like to override your existing checks and extraction configurations with Persona's recommended configuration for social security cards?"
          cancelAction={hideOverridePromptModal}
          confirmAction={() => {
            hideOverridePromptModal();
            overrideVerificationChecksAndExtractionsByDocumentType(
              DocumentType.SocialSecurityCardRestricted,
            );
          }}
          cancelActionText='No'
          confirmActionText='Yes'
        >
          This action will overwrite your existing configurations.
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

export default DocumentTypeConfigurationElement;
